import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

const ARTICLE_IMAGES: Record<string, string> = {
  'how-to-access-91-web': 'https://readdy.ai/api/search-image?query=Modern%20laptop%20displaying%20a%20clean%20web%20browser%20interface%20with%20login%20page%2C%20minimalist%20desk%20setup%20with%20warm%20ambient%20lighting%2C%20soft%20natural%20light%20from%20window%2C%20editorial%20photography%20style%20with%20shallow%20depth%20of%20field%2C%20warm%20neutral%20tones&width=1200&height=600&seq=detail-access&orientation=landscape',
  'fix-91-web-not-opening': 'https://readdy.ai/api/search-image?query=A%20person%20troubleshooting%20on%20laptop%20with%20browser%20error%20page%20displayed%2C%20warm%20modern%20home%20office%20setting%20with%20plants%20on%20desk%2C%20soft%20daylight%20through%20blinds%2C%20editorial%20atmosphere%2C%20warm%20beige%20and%20wood%20tones&width=1200&height=600&seq=detail-fix&orientation=landscape',
  '91-web-new-features-2026': 'https://readdy.ai/api/search-image?query=Modern%20clean%20web%20interface%20design%20showcase%20on%20a%20large%20monitor%20screen%2C%20bright%20airy%20creative%20office%20environment%2C%20minimal%20Scandinavian%20style%20desk%20setup%2C%20natural%20lighting%2C%20warm%20neutral%20color%20palette&width=1200&height=600&seq=detail-features&orientation=landscape',
  '91-web-speed-optimization': 'https://readdy.ai/api/search-image?query=Close-up%20of%20a%20laptop%20keyboard%20with%20hands%20typing%2C%20speed%20and%20efficiency%20theme%2C%20modern%20minimalist%20desk%20setup%2C%20soft%20warm%20lighting%2C%20shallow%20depth%20of%20field%2C%20warm%20beige%20and%20cream%20tones&width=1200&height=600&seq=detail-speed&orientation=landscape',
  '91-web-account-security': 'https://readdy.ai/api/search-image?query=Digital%20security%20concept%20with%20padlock%20icon%20floating%20over%20a%20laptop%20screen%2C%20modern%20cyber%20security%20theme%2C%20clean%20desk%20setting%2C%20warm%20masculine%20tones%20with%20soft%20lighting%2C%20professional%20corporate%20atmosphere&width=1200&height=600&seq=detail-security&orientation=landscape',
  '91-web-complete-platform-guide': 'https://readdy.ai/api/search-image?query=Comprehensive%20digital%20dashboard%20displayed%20on%20an%20ultra-wide%20monitor%2C%20organized%20interface%20with%20multiple%20panels%2C%20modern%20creative%20workspace%2C%20warm%20ambient%20lighting%2C%20professional%20tech%20environment%2C%20clean%20minimalist&width=1200&height=600&seq=detail-guide&orientation=landscape',
  '91-web-vs-app-comparison': 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20showing%20web%20browser%20on%20left%20and%20mobile%20phone%20on%20right%20displaying%20same%20platform%20interface%2C%20modern%20desk%20setup%2C%20warm%20natural%20light%2C%20balanced%20composition&width=1200&height=600&seq=detail-compare&orientation=landscape',
  '91-web-register-guide': 'https://readdy.ai/api/search-image?query=Fresh%20start%20concept%20with%20a%20laptop%20showing%20registration%20form%20on%20screen%2C%20clean%20modern%20desk%20with%20a%20small%20plant%20and%20coffee%20cup%2C%20soft%20morning%20light%20streaming%20through%20window%2C%20warm%20inviting%20atmosphere&width=1200&height=600&seq=detail-register&orientation=landscape',
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                <i className="ri-file-unknow-line text-2xl"></i>
              </div>
              <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <div className="pt-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  发布于 {article.publishDate}
                </span>
                {article.updateDate !== article.publishDate && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-refresh-line"></i>
                    更新于 {article.updateDate}
                  </span>
                )}
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-10">
              <img
                src={ARTICLE_IMAGES[article.slug] || ARTICLE_IMAGES['how-to-access-91-web']}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-heading [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3
                [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                [&_ul]:mb-4 [&_ul]:pl-5 [&_ul_li]:mb-1.5 [&_ul_li]:text-foreground-700
                [&_ol]:mb-4 [&_ol]:pl-5 [&_ol_li]:mb-1.5 [&_ol_li]:text-foreground-700
                [&_strong]:text-foreground-900 [&_strong]:font-medium
                [&_a]:text-primary-600 [&_a]:no-underline hover:[&_a]:underline"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-heading text-foreground-900 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articles
                  .filter((a) => a.slug !== article.slug && a.category === article.category)
                  .slice(0, 2)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={ARTICLE_IMAGES[related.slug] || ARTICLE_IMAGES['how-to-access-91-web']}
                          alt={related.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mb-1">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500">{related.readTime} 分钟阅读 · {related.publishDate}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            dateModified: article.updateDate,
            image: ARTICLE_IMAGES[article.slug] || ARTICLE_IMAGES['how-to-access-91-web'],
            url: `https://91web.example.com/article/${article.slug}`,
            publisher: {
              '@type': 'Organization',
              name: '91网页版入口',
            },
            inLanguage: 'zh-CN',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91web.example.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://91web.example.com/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
                item: `https://91web.example.com/article/${article.slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}